/**
 * Take an avatar photo with the device camera (getUserMedia). Works on the
 * desktop shell's webview + laptops with a webcam + mobile. Degrades
 * gracefully: if the camera is unavailable or permission is denied, it
 * shows a clear message and the caller's "Upload photo" path is still
 * there. Captured frame → a JPEG `File` handed back via `onCapture`.
 */
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import { useEffect, useRef, useState } from "react";

export function CapturePhotoDialog({
  onCapture,
  onClose,
}: {
  onCapture: (file: File) => void;
  onClose: () => void;
}): JSX.Element {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const md = typeof navigator !== "undefined" ? navigator.mediaDevices : undefined;
    if (!md?.getUserMedia) {
      setError("This device has no camera available. Use “Upload photo” instead.");
      return;
    }
    md.getUserMedia({ video: { facingMode: "user" }, audio: false })
      .then((stream) => {
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          void videoRef.current.play().catch(() => {});
        }
        setReady(true);
      })
      .catch(() => {
        if (!cancelled) {
          setError("Couldn't access the camera. Check permissions, or use “Upload photo”.");
        }
      });
    return () => {
      cancelled = true;
      streamRef.current?.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    };
  }, []);

  function snap(): void {
    const video = videoRef.current;
    if (!video) return;
    const w = video.videoWidth || 480;
    const h = video.videoHeight || 480;
    // Center-crop to a square so the round avatar isn't distorted.
    const side = Math.min(w, h);
    const canvas = document.createElement("canvas");
    canvas.width = side;
    canvas.height = side;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.drawImage(video, (w - side) / 2, (h - side) / 2, side, side, 0, 0, side, side);
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          setError("Could not capture the photo. Try again.");
          return;
        }
        onCapture(new File([blob], "avatar.jpg", { type: "image/jpeg" }));
      },
      "image/jpeg",
      0.9,
    );
  }

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Take a photo</DialogTitle>
      <DialogContent>
        {error ? (
          <Typography sx={{ fontSize: 14, color: "error.main" }}>{error}</Typography>
        ) : (
          <Box
            sx={{
              position: "relative",
              width: "100%",
              aspectRatio: "1 / 1",
              borderRadius: 2,
              overflow: "hidden",
              bgcolor: "common.black",
            }}
          >
            {/* Live camera preview — captions do not apply to a capture feed. */}
            <video
              ref={videoRef}
              playsInline
              muted
              style={{ width: "100%", height: "100%", objectFit: "cover" }}
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        {!error && (
          <Button variant="contained" disableElevation onClick={snap} disabled={!ready}>
            Capture
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
